import { Fragment, useState, useMemo, type ReactElement } from 'react';
import { Badge, ModalBody, Tooltip } from '@vd/ui';
import { PerformanceTab } from './PerformanceTab';
import { Maximize2, Search, Terminal, Network, AlertTriangle, Gauge } from 'lucide-react';
import { PERFORMANCE_BOUNDS, type Ticket, type ConsoleMessage, type NetworkRequest, type CapturedError, type PerformanceSnapshot } from '@vd/types';
import { TICKET_SCREEN } from '../../lib/screenNames';
import styles from './DevToolsPanel.module.css';
import { formatClock, formatFull } from '../../lib/time';

// The session is the debugging window's other tab (DebugWindow.tsx, 24 Oct), not one of these.
export type TabKey = 'console' | 'network' | 'errors' | 'performance';
const TAB_KEYS: readonly TabKey[] = ['console', 'network', 'errors', 'performance'];

/** A tab named in the address (`?devtools=errors`), or null for any other value (TOADO-852). */
export function devToolsTabFrom(value: string | null): TabKey | null {
  return (TAB_KEYS as readonly string[]).includes(value ?? '') ? value as TabKey : null;
}

export interface DevToolsPanelProps {
  ticket: Ticket;
  /** Expand: open the debugging window on this tab (24 Oct: one window, with Session beside DevTools). */
  onExpand: (tab: TabKey) => void;
}

// ---------------------------------------------------------------------------
// DevTools data extraction. Tickets without captured data show empty states
// per tab — earlier versions filled with demo fixtures, but on a freshly-
// created blank ticket those looked like real data and confused users.
// ---------------------------------------------------------------------------

const EMPTY_PERF: PerformanceSnapshot = { longTasks: [] };

type DevData = {
  console: ConsoleMessage[];
  network: NetworkRequest[];
  errors: CapturedError[];
  performance: PerformanceSnapshot;
  hasPerf: boolean;
};

function extract(ticket: Ticket): DevData {
  const t = ticket as Ticket & {
    console?: ConsoleMessage[]; network?: NetworkRequest[]; errors?: CapturedError[]; performance?: unknown;
  };
  const performance = readPerformance(t.performance);
  return {
    console: t.console ?? [],
    network: t.network ?? [],
    errors: t.errors ?? [],
    performance: performance ?? EMPTY_PERF,
    hasPerf: hasReadings(performance),
  };
}

/**
 * The stored performance object, keeping only readings a page could have. The
 * server stores what a client sent, and the JSON capture route takes any
 * shape: a `cls` that is a string would throw in `toFixed` and take the ticket
 * page down for everyone who opened it, a list of a million long tasks would
 * be walked in full to show ten, and a negative time would be shown as one.
 * The ranges are the extension's (drainResponse.ts).
 */
const { maxTimingMs: MAX_TIMING_MS, maxCls: MAX_CLS, maxLongTasks: MAX_LONG_TASKS } = PERFORMANCE_BOUNDS;
function readPerformance(value: unknown): PerformanceSnapshot | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const raw = value as Record<string, unknown>;
  const within = (reading: unknown, max: number) =>
    typeof reading === 'number' && Number.isFinite(reading) && reading >= 0 && reading <= max ? reading : undefined;
  const snapshot: PerformanceSnapshot = {
    // The newest, as the extension keeps them: the stall nearest the bug.
    longTasks: (Array.isArray(raw.longTasks) ? raw.longTasks.slice(-MAX_LONG_TASKS) : []).flatMap((task: unknown) => {
      const fields = task && typeof task === 'object' ? task as Record<string, unknown> : {};
      // Time since the page loaded, which a tab open for days passes a day
      // of: only a real, non-negative number is asked of it (hotfix review).
      const startTime = within(fields.startTime, Number.MAX_SAFE_INTEGER);
      const durationMs = within(fields.durationMs, MAX_TIMING_MS);
      return startTime === undefined || durationMs === undefined ? [] : [{ startTime, durationMs }];
    }),
  };
  for (const metric of ['lcp', 'fcp', 'inp', 'ttfb', 'domContentLoaded'] as const) {
    const reading = within(raw[metric], MAX_TIMING_MS);
    if (reading !== undefined) snapshot[metric] = reading;
  }
  const cls = within(raw.cls, MAX_CLS);
  if (cls !== undefined) snapshot.cls = cls;
  // 0 is a load that had not finished: v0.1.4 reports it for a capture taken
  // before the load event, and it is not a load time.
  const navigation = raw.navigation && typeof raw.navigation === 'object' ? raw.navigation as Record<string, unknown> : undefined;
  const loadEvent = within(navigation?.loadEvent, MAX_TIMING_MS);
  if (loadEvent !== undefined && loadEvent > 0) snapshot.navigation = { loadEvent };
  return snapshot;
}

/**
 * Did the SW successfully drain the buffer for this category? "Yes"
 * + an empty array means the buffer was empty at capture time — most
 * commonly because activity happened in an iframe or worker, or
 * before the content script wrappers were in place. The empty-state
 * component renders an explainer in that case so users don't think
 * the feature is broken.
 */
function drainSucceededFor(ticket: Ticket, key: 'console' | 'network' | 'errors'): boolean {
  return !!ticket.healthDetail?.captured?.[key];
}

/**
 * Parse a topUrl once per render and return the host. Used by NetworkTab
 * and ErrorsTab to feed frameLabel() without re-parsing the same string
 * once per row. On a 500-row network table this saved ~5ms of jank on
 * the expand-modal open per the v0.1.4 perf sweep.
 */
function parseTopHost(topUrl: string): string {
  try {
    return new URL(topUrl).host;
  } catch {
    return '';
  }
}

/**
 * Normalize a frame's URL into a short label suitable for a badge.
 * Returns null when the entry is from the captured page's top frame
 * (caller suppresses the badge entirely in that case to avoid visual
 * noise). Returns the host of the iframe URL otherwise. Older v0.1.3
 * captures predate frame stamping and have no `frameUrl` — those also
 * return null so a mixed corpus of old + new tickets renders cleanly.
 *
 * `topHost` is precomputed by the caller via parseTopHost() so we don't
 * re-parse the same topUrl once per row — see the perf-sweep notes.
 */
function frameLabel(frameUrl: string | undefined, topUrl: string, topHost: string): string | null {
  if (!frameUrl) return null;
  if (frameUrl === topUrl) return null;
  try {
    const u = new URL(frameUrl);
    const fHost = u.host;
    // Exact host match only — subdomain iframes (cdn.example.com inside
    // app.example.com) still get a label because they're a different
    // host and worth distinguishing during debugging. Suppress only when
    // the iframe is literally same-host as the top frame.
    if (fHost && fHost === topHost) return null;
    if (fHost) return fHost;
    // Empty host happens for about:blank, blob:, data:, javascript:,
    // srcdoc, and a handful of other schemes whose URL has no
    // authority component. Render the scheme as the label instead of
    // emitting an empty "iframe:" badge that confuses readers. The
    // protocol from URL parsing always ends in ":"; strip it.
    const proto = u.protocol.replace(/:$/, '');
    return proto || null;
  } catch {
    return null;
  }
}

function levelCounts(rows: ConsoleMessage[]) {
  const c = { log: 0, warn: 0, err: 0 };
  for (const r of rows) {
    if (r.level === 'error') c.err++;
    else if (r.level === 'warn') c.warn++;
    else c.log++;
  }
  return c;
}

function devToolsTabs(data: DevData): Array<{ key: TabKey; label: string; short: string; count?: number; icon: ReactElement }> {
  return [
    { key: 'console',     label: 'Console',     short: 'Log',  count: data.console.length,  icon: <Terminal size={12} /> },
    { key: 'network',     label: 'Network',     short: 'Net',  count: data.network.length,  icon: <Network size={12} /> },
    { key: 'errors',      label: 'Errors',      short: 'Err',  count: data.errors.length,   icon: <AlertTriangle size={12} /> },
    { key: 'performance', label: 'Performance', short: 'Perf', icon: <Gauge size={12} /> },
  ];
}

/** DevTools' tabs with their counts, for the debugging window's one row of tabs (Jason, 24 Oct). */
export function devToolsTabsFor(ticket: Ticket): Array<{ key: TabKey; label: string; count?: number; icon: ReactElement }> {
  return devToolsTabs(extract(ticket));
}

export function DevToolsPanel({ ticket, onExpand }: DevToolsPanelProps) {
  const [tab, setTab] = useState<TabKey>('console');
  const data = useMemo(() => extract(ticket), [ticket]);
  const tabs = devToolsTabs(data);
  /**
   * Each tab is a screen of the ticket's for pins (TOADO-701), in the panel
   * and in the expanded view alike. The tab bar is the ticket's, the same
   * whichever tab is open.
   */
  const screen = `${TICKET_SCREEN}/devtools/${tab}`;

  return (
    <>
      <div className={styles.panel} data-toado-view={screen}>
        <div className={styles.tabBar} data-toado-view={TICKET_SCREEN}>
          <div className={styles.tabs} role="tablist">
            {tabs.map((t) => (
              <Tooltip key={t.key} content={t.label}>
                <button
                  role="tab"
                  aria-selected={tab === t.key}
                  className={tab === t.key ? `${styles.tab} ${styles.tabActive}` : styles.tab}
                  onClick={() => setTab(t.key)}
                >
                  {t.icon}
                  <span className={styles.tabLabel}>{t.short}</span>
                  {t.count !== undefined && <span className={styles.tabCount}>{t.count}</span>}
                </button>
              </Tooltip>
            ))}
          </div>
          <Tooltip content="Expand">
            <button className={styles.expandBtn} onClick={() => onExpand(tab)} aria-label="Expand debugging context">
              <Maximize2 size={13} />
            </button>
          </Tooltip>
        </div>
        {/* Severity pills (log / warn / err counts) are scoped to the
            console stream. Showing them on the Errors / Network /
            Performance tabs was confusing, especially for the Err tab
            where the pill `err N` and the tab `Err M` count different
            things (console.error calls vs runtime errors). Render the
            filters only on the console tab where they apply. */}
        {tab === 'console' && <Filters console={data.console} />}
        <TabBody tab={tab} compact data={data} ticket={ticket} />
      </div>
    </>
  );
}

/**
 * DevTools at full size: one of the debugging window's tabs (24 Oct). The
 * window's header carries the tabs, Session, Console, Network, Errors and
 * Performance in one row (Jason, 24 Oct), so this is the tab's filters and its
 * body.
 */
export function DevToolsView({ ticket, tab, bodyHeight }: {
  ticket: Ticket;
  tab: TabKey;
  bodyHeight: string;
}) {
  const data = useMemo(() => extract(ticket), [ticket]);
  return (
    <>
      {tab === 'console' && <div className={styles.modalFixedHead}><Filters console={data.console} /></div>}
      <ModalBody style={{ flex: `0 0 ${bodyHeight}`, height: bodyHeight }} data-toado-view={`${TICKET_SCREEN}/devtools/${tab}`}>
        <TabBody tab={tab} compact={false} data={data} ticket={ticket} />
      </ModalBody>
    </>
  );
}

function Filters({ console: rows }: { console: ConsoleMessage[] }) {
  const c = levelCounts(rows);
  return (
    <div className={styles.panelHead}>
      <div className={styles.filter}>
        <Search size={12} />
        <input placeholder="Filter…" />
      </div>
      <div className={styles.severities}>
        <Badge variant="outline">log {c.log}</Badge>
        <Badge variant="warning" dot>warn {c.warn}</Badge>
        <Badge variant="danger" dot>err {c.err}</Badge>
      </div>
    </div>
  );
}

function TabBody({ tab, compact, data, ticket }: { tab: TabKey; compact: boolean; data: DevData; ticket: Ticket }) {
  const topUrl = ticket.sourceUrl ?? '';
  if (tab === 'console')     return <ConsoleTab compact={compact} rows={data.console} drainSucceeded={drainSucceededFor(ticket, 'console')} listening={listeningSpan(ticket)} capturedAt={ticket.createdAt} />;
  if (tab === 'network')     return <NetworkTab compact={compact} rows={data.network} drainSucceeded={drainSucceededFor(ticket, 'network')} topUrl={topUrl} />;
  if (tab === 'errors')      return <ErrorsTab rows={data.errors} drainSucceeded={drainSucceededFor(ticket, 'errors')} topUrl={topUrl} />;
  if (!data.hasPerf && data.network.length === 0) return <EmptyTabState label="performance metrics" />;
  return <PerformanceTab perf={data.performance} hasPerf={data.hasPerf} network={data.network} pageUrl={topUrl || undefined} />;
}

/**
 * A console line's time in the person's zone, to the millisecond (TOADO-904;
 * it used to be cut out of the UTC ISO string), with how long before the
 * capture it came on hover, which is often what someone debugging wants.
 */
function LineTime({ at, capturedAt }: { at: number; capturedAt?: string }) {
  const captured = capturedAt ? new Date(capturedAt).getTime() : NaN;
  const before = Number.isFinite(captured) && Number.isFinite(at) ? (captured - at) / 1000 : null;
  const offset = before === null ? '' : before >= 0 ? ` · ${before.toFixed(1)} s before capture` : ` · ${(-before).toFixed(1)} s after capture`;
  return (
    <time className={styles.time} dateTime={new Date(at).toISOString()} title={`${formatFull(at)}${offset}`}>{formatClock(at)}</time>
  );
}

/**
 * When a widget ticket's widget was listening to the page's console, from its
 * start to the report (TOADO-910), or null for any other ticket, including a
 * Minimal widget's, which does not listen at all.
 */
function listeningSpan(ticket: Ticket): { since: number } | null {
  const since = ticket.capture?.collectingSince;
  if (typeof since !== 'number' || !Number.isFinite(since)) return null;
  const reported = Date.parse(ticket.createdAt);
  // The visitor's clock may run a little ahead of the server's; the API takes
  // five minutes of that (collectingSince.ts), and so does this (Codex, round 3).
  return Number.isFinite(reported) && since <= reported + 5 * 60 * 1000 ? { since } : null;
}

function EmptyTabState({ label, drainSucceeded }: { label: string; drainSucceeded?: boolean }) {
  return (
    <div style={{ padding: 16, color: 'var(--text-tertiary)', fontSize: 13, lineHeight: 1.5 }}>
      <div>No {label} captured for this ticket.</div>
      {drainSucceeded && (
        <div style={{ marginTop: 8, fontSize: 12 }}>
          Toado drained the buffer successfully. It was empty at capture time. Common causes:
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            <li>Activity happened in a <strong>web/service worker</strong> (separate context).</li>
            <li>Activity happened in a <strong>sandboxed iframe</strong> (Chrome blocks content scripts there).</li>
            <li>Errors fired <strong>before the extension's content script attached</strong>. This usually means the tab was open when the extension was reloaded or installed. Reloading the tab once seeds the wrappers for next time.</li>
          </ul>
        </div>
      )}
    </div>
  );
}

function ConsoleTab({ compact, rows, drainSucceeded, listening, capturedAt }: {
  compact: boolean; rows: ConsoleMessage[]; drainSucceeded: boolean; listening: { since: number } | null; capturedAt?: string;
}) {
  // A widget that was listening heard nothing: say since when, rather than
  // "not captured", which is the extension's case (TOADO-910).
  if (rows.length === 0 && listening) {
    return (
      <div style={{ padding: 16, color: 'var(--text-tertiary)', fontSize: 13, lineHeight: 1.5 }}>
        Nothing was logged between {formatClock(listening.since, { ms: false })} (when the widget started listening) and the report.
      </div>
    );
  }
  if (rows.length === 0) return <EmptyTabState label="console messages" drainSucceeded={drainSucceeded} />;
  return (
    <div className={compact ? styles.console : styles.consoleExpanded}>
      {rows.slice(0, compact ? 20 : 500).map((r, i) => (
        <div key={i} className={`${styles.row} ${styles[r.level]}`}>
          <LineTime at={r.timestamp} capturedAt={capturedAt} />
          <span className={styles.level}>{r.level}</span>
          <div>
            <div className={styles.msg}>{r.message}</div>
            {r.source && <div className={styles.src}>{r.source}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function NetworkTab({ compact, rows, drainSucceeded, topUrl }: { compact: boolean; rows: NetworkRequest[]; drainSucceeded: boolean; topUrl: string }) {
  if (rows.length === 0) return <EmptyTabState label="network requests" drainSucceeded={drainSucceeded} />;
  // Parse topUrl once. Without this, frameLabel re-parses it on every
  // row of a 500-row expanded view — measurable jank on heavy captures.
  const topHost = parseTopHost(topUrl);
  return (
    <div className={compact ? styles.tableWrap : styles.tableWrapExpanded}>
      <table className={styles.table}>
        <thead><tr><th>Method</th><th>URL</th><th>Status</th><th>Time</th></tr></thead>
        <tbody>
          {rows.slice(0, compact ? 20 : 500).map((r) => {
            const fLabel = frameLabel(r.frameUrl, topUrl, topHost);
            return (
              <Fragment key={r.id}>
              <tr>
                {/* Method / status are absent on widget "standard" tier
                    entries, which are captured passively via
                    PerformanceObserver and genuinely carry neither (getting
                    them needs the fetch-wrap of the deep tier). Extension
                    and deep-tier captures always have a numeric status, so
                    their rendering is unchanged. Without this guard a
                    passive entry rendered `undefined < 400` → a red
                    "danger" badge with empty text on every row, making a
                    successful request's network tab look broken. */}
                <td className={styles.mono}>{r.method || '-'}</td>
                <td className={styles.mono}>
                  {r.url}
                  {fLabel && <Badge variant="outline" className={styles.frameBadge}>iframe: {fLabel}</Badge>}
                </td>
                <td>
                  {typeof r.status !== 'number' ? (
                    <Badge variant="outline">timing</Badge>
                  ) : r.status === 0 ? (
                    <Badge variant="danger">failed</Badge>
                  ) : (
                    <Badge variant={r.status < 400 ? 'success' : 'danger'}>{r.status}</Badge>
                  )}
                </td>
                <td className={styles.mono}>
                  {r.durationMs}ms{r.bodyTruncated && <span className={styles.truncated}> · body truncated</span>}
                </td>
              </tr>
              {!compact && (
                <tr>
                  <td colSpan={4} className={styles.networkDetailsCell}>
                    <details>
                      <summary>Request details</summary>
                      <div className={styles.networkDetailsGrid}>
                        <section>
                          <strong>Request headers</strong>
                          <pre>{JSON.stringify(r.requestHeaders ?? {}, null, 2)}</pre>
                        </section>
                        <section>
                          <strong>Response headers</strong>
                          <pre>{JSON.stringify(r.responseHeaders ?? {}, null, 2)}</pre>
                        </section>
                        <section>
                          <strong>Request body</strong>
                          <pre>{r.requestBody ?? 'Not captured in safe mode.'}</pre>
                        </section>
                        <section>
                          <strong>Response body</strong>
                          <pre>{r.responseBody ?? 'Not captured in safe mode.'}</pre>
                        </section>
                      </div>
                    </details>
                  </td>
                </tr>
              )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ErrorsTab({ rows, drainSucceeded, topUrl }: { rows: CapturedError[]; drainSucceeded: boolean; topUrl: string }) {
  if (rows.length === 0) return <EmptyTabState label="JS errors" drainSucceeded={drainSucceeded} />;
  const topHost = parseTopHost(topUrl);
  return (
    <div className={styles.errorsList}>
      {rows.map((e, i) => {
        const fLabel = frameLabel(e.frameUrl, topUrl, topHost);
        return (
          <div key={i} className={styles.errorCard}>
            <div className={styles.errorHead}>
              <Badge variant="danger">×{e.count}</Badge>
              <code>{e.message}</code>
              {fLabel && <Badge variant="outline" className={styles.frameBadge}>iframe: {fLabel}</Badge>}
            </div>
            {e.stack && <div className={styles.errorStack}>{e.stack}</div>}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Whether a snapshot holds any reading. Pins used to send `{ longTasks: [] }`
 * and the tab showed it as a row of dashes, as though something had been
 * measured; a snapshot with nothing in it is shown as not captured.
 */
function hasReadings(perf: PerformanceSnapshot | undefined): boolean {
  if (!perf) return false;
  return perf.lcp !== undefined || perf.fcp !== undefined || perf.cls !== undefined || perf.inp !== undefined
    || perf.ttfb !== undefined || perf.domContentLoaded !== undefined
    || perf.navigation !== undefined || perf.longTasks.length > 0;
}
